import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { Rectangle, useMap } from 'react-leaflet';

import {
    clampBox, isValidBox, requestBox, boxContains, enlargeBox, intersectBox,
} from '../../lib/coverageBounds';
import {
    trailSignature, isSameTracksGrown, buildTrail, isOnTrail, fillHoles,
} from '../../lib/coverageCorridor';

const PANE_NAME = 'coverage';
// Above the map tiles (200), below the route and the trails (Leaflet's overlay
// pane, 400). Drawn over the tracks, the squares tinted them and were taken
// for tracks themselves.
const PANE_Z_INDEX = 350;
// Enlarged squares overlap their neighbours. Each square is drawn solid and
// the layer as a whole is made see-through, so overlaps do not add up to
// almost solid colour.
const LAYER_OPACITY = 0.3;
// The route line is 10 px wide and lies on top. A square must be wider to
// show on both sides of it.
const MIN_SQUARE_PX = 16;
const COLORS = {
    good: '#198754',
    bad: '#dc3545',
};
// Drawn in this order, so red ends up on top where squares overlap.
const DRAW_ORDER = ['good', 'bad'];
// Tracks grow with every position that comes in. Walking through all of them
// takes time, so a trail made of tracks is worked out at most this often.
const TRACKS_REBUILD_MS = 60000;

function viewBox(map) {
    const bounds = map.getBounds();
    return clampBox({
        south: bounds.getSouth(),
        west: bounds.getWest(),
        north: bounds.getNorth(),
        east: bounds.getEast(),
    });
}

class CoverageLayer extends Component {
    constructor(props) {
        super(props);
        const { map } = props;
        this.state = {
            cells: [],
            zoom: map.getZoom(),
            // Filled in by getDerivedStateFromProps before the first render.
            signature: null,
            trail: null,
            route: null,
            trailBox: null,
            trailBuilt: 0,
        };
        this.fetchedBox = null;
        this.requestNo = 0;
        this.unmounted = false;

        // The rectangles are drawn into this pane, so it has to exist before
        // the first render. It stays on the map when the overlay is turned
        // off, so reuse it.
        const pane = map.getPane(PANE_NAME) || map.createPane(PANE_NAME);
        pane.style.zIndex = PANE_Z_INDEX;
        pane.style.opacity = LAYER_OPACITY;
        pane.style.pointerEvents = 'none';
    }

    // The grid holds positions from every race. Only the squares along the
    // trail shown on the map are drawn: the route file of the class, or the
    // tracks of the contestants when the class has no route file.
    static getDerivedStateFromProps(props, state) {
        const { files, trackers } = props;
        const signature = trailSignature(files, trackers);
        if (signature === state.signature) {
            return null;
        }
        const now = Date.now();
        const fromTracks = signature.startsWith('tracks:');
        // Only tracks that have grown can wait. An empty trail is never held
        // on to: the positions may have been on their way when the layer was
        // turned on.
        const hasTrail = state.trail && state.trail.size > 0;
        const canWait = hasTrail && isSameTracksGrown(state.signature, signature);
        if (canWait && now - state.trailBuilt < TRACKS_REBUILD_MS) {
            return null;
        }
        const trail = buildTrail(files, trackers);
        return {
            signature,
            trail: trail.cells,
            route: trail.route,
            trailBox: trail.box,
            trailBuilt: fromTracks ? now : 0,
        };
    }

    componentDidMount() {
        const { map } = this.props;
        map.on('moveend', this.loadForView);
        map.on('zoomend', this.onZoom);
        this.loadForView();
    }

    // The tracking map re-renders every second while auto-update counts down.
    // Only new cells, a new zoom or a new trail are a reason to draw the
    // squares again.
    shouldComponentUpdate(nextProps, nextState) {
        const { map } = this.props;
        const { cells, zoom, trail } = this.state;
        return nextState.cells !== cells || nextState.zoom !== zoom || nextState.trail !== trail
            || nextProps.map !== map;
    }

    componentDidUpdate(prevProps, prevState) {
        const { trail } = this.state;
        if (prevState.trail !== trail) {
            // Another class, or tracks that have reached a new area.
            this.loadForView();
        }
    }

    componentWillUnmount() {
        const { map } = this.props;
        this.unmounted = true;
        map.off('moveend', this.loadForView);
        map.off('zoomend', this.onZoom);
    }

    onZoom = () => {
        const { map } = this.props;
        this.setState({ zoom: map.getZoom() });
    }

    loadForView = async () => {
        const { map } = this.props;
        const { trailBox } = this.state;
        const view = viewBox(map);
        if (!isValidBox(view) || !trailBox) {
            return;
        }
        // Squares are drawn along the trail only, so only the part of the
        // view that holds the trail is needed.
        const needed = intersectBox(view, trailBox);
        if (!isValidBox(needed) || boxContains(this.fetchedBox, needed)) {
            return;
        }

        const box = intersectBox(requestBox(view), trailBox);
        this.requestNo += 1;
        const { requestNo } = this;

        // publish: false keeps a failed request from showing an error message
        // to the public. The map works fine without the overlay.
        const response = await util.fetchApi('/api/gpscoverages/public', { method: 'GET', publish: false }, box);

        // A newer request was sent, or the overlay was turned off, while this
        // one was under way.
        if (this.unmounted || requestNo !== this.requestNo) {
            return;
        }
        if (response?.status !== 200 || !Array.isArray(response.data?.cells)) {
            return;
        }
        // A cut-off answer does not cover the whole box, so it is not
        // remembered: zooming in has to ask again for the smaller area.
        this.fetchedBox = response.data.truncated ? null : box;
        this.setState({ cells: response.data.cells });
    }

    render() {
        const { cells, zoom, trail, route } = this.state;
        const onTrail = cells.filter(cell => isOnTrail(cell, trail));
        // Cells without colour leave holes in the band once the squares have
        // their true size. Short holes along the route are filled from the
        // nearest squares.
        const squares = [...onTrail, ...fillHoles(onTrail, route)];
        return (<>
            {DRAW_ORDER.map(drawn => squares
                .filter(cell => cell[4] === drawn)
                .map(([south, west, north, east, status]) => {
                    const box = enlargeBox({
                        south, west, north, east,
                    }, zoom, MIN_SQUARE_PX);
                    return (
                        <Rectangle
                            key={`coverage-${south}-${west}`}
                            bounds={[[box.south, box.west], [box.north, box.east]]}
                            pane={PANE_NAME}
                            stroke={false}
                            fillColor={COLORS[status]}
                            fillOpacity={1}
                            interactive={false}
                        />
                    );
                }))}
        </>);
    }
}

// files: the files of the race class shown, with the route as geoJSON.
// trackers: the trackers with the positions the map has loaded.
const CoverageOverlay = ({ files, trackers }) => {
    const map = useMap();
    return <CoverageLayer map={map} files={files} trackers={trackers} />;
};

export default CoverageOverlay;
